import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "@/Utility/Input/Input";
import Dropdown from "@/Utility/Dropdown/Dropdown";
import SingleDatePicker from "@/Utility/DatePickerRange/SingleDatePicker";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "@/utils/dateUtils";
import FileDropzone from "@/Utility/FileDropzone/FileDropzone";
import DescriptionEditor from "@/Utility/DescriptionEditor/DescriptionEditor";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buildCreateTaskSchema } from "@/FormsValidations/Validations";
import { isWorkItemVersionConflictError } from "@/utils/workItemVersionConflict";
import ErrorMsg from "@/Utility/ErrorMsg";
import Typography from "@/Utility/Typography";
import Checkbox from "@/Utility/Checkbox/Checkbox";
import { isClientOnHold } from "@/constants/clientOnHoldRules";
import {
  fetchProjectsData,
  fetchWorkItemById,
  updateWorkItem,
} from "@/Redux/Thunks/projectManagementThunk";
import { get } from "@/Services/ApiBase";
import { API_ENDPOINTS } from "@/Services/ApiEndPoints";
import { normalizeWorkItemFromApi } from "@/Pages/Projects/projectManagementNormalizers";
import { mapWorkItemFromApiPayload } from "@/Pages/Projects/projectMappers";
import { fetchClientsData } from "@/Redux/Thunks/clientsThunk";
import { fetchSystemList } from "@/Redux/Thunks/systemListsThunk";
import { fetchUsersData } from "@/Redux/Thunks/usersThunk";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import { getProjectWorkflowStatusesList } from "@/constants/workItemStatusCategoryTheme";
import toast from "react-hot-toast";
import {
  parseWorkItemEstimateInputToMinutes,
  sanitizeWorkItemEstimateInput,
} from "@/utils/workItemEstimateInput";
import {
  selectProjectsData,
  selectProjectsAllListData,
  selectProjectsLoading,
} from "@/Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "@/Redux/Slices/clientsSlice";
import { selectUsersData } from "@/Redux/Slices/usersSlice";
import {
  getListFromApiResponse,
  preferListFromPropOrStore,
  getAllowedProjects,
  mapProjectToParentOption,
  mapUserToAssigneeOptionForDropdown,
  filterAssigneesForWorkItemType,
  isEpicWorkItemTypeLabel,
  mapSystemListItemToOption,
  TYPE_ICON_MAP,
  TYPE_FALLBACK_OPTIONS,
  PRIORITY_ICON_MAP,
  PRIORITY_FALLBACK_OPTIONS,
  resolveDefaultMediumPriorityId,
  ENVIRONMENT_FALLBACK_OPTIONS,
  REASON_OF_BUG_OPTIONS,
  LABEL_FALLBACK_OPTIONS,
} from "@/Pages/Issues/issueMappers";
import { mapWorkItemToCreateTaskDefaults } from "@/Pages/Issues/CreateModal/mapWorkItemToCreateTaskDefaults";
import {
  buildUpdateWorkItemBodyFromBacklogForm,
  extractUpdateBaselineFromWorkItem,
} from "./buildBacklogUpdateWorkItemBody";
import { getThunkRejectMessage } from "@/utils/thunkErrorMessage";

const getTodayLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function buildBacklogFormEmptyValues(projectIdMaybe) {
  const pid =
    projectIdMaybe != null && String(projectIdMaybe).trim() !== ""
      ? String(projectIdMaybe).trim()
      : "";
  return {
    type: "",
    project: pid,
    parentId: "",
    priority: "",
    assignee: "",
    summary: "",
    attachments: [],
    originalEstimate: "",
    storyPoints: "",
    startDate: getTodayLocalDate(),
    dueDate: "",
    severityId: "",
    environment: [],
    reproductionSteps: "",
    reasonOfBug: "",
    isBacklog: true,
    label: "",
  };
}

/**
 * Same fields/layout as create-task, but submits {@link updateWorkItem} for an existing row (backlog tab).
 */
const BacklogTaskForm = ({
  projectsList: projectsListProp,
  clientsList: clientsListProp,
  currentProjectId,
  /** `{ projectId, workItemId, normalizedWorkItem? }` — drives prefill + PATCH target */
  editContext = null,
  onSuccess,
  onSubmittingChange,
}) => {
  const dispatch = useDispatch();
  /** Paginated list vs GET /projects?all — full list lives in `allListData` only (see projectsManagementSlice). */
  const projectsPagedData = useSelector(selectProjectsData);
  const projectsAllListData = useSelector(selectProjectsAllListData);
  /** Merge paged + full list so new projects (often only on paged slice) still resolve labels in dropdowns. */
  const projectsFromStore = useMemo(() => {
    const allList = getListFromApiResponse(projectsAllListData);
    const pagedList = getListFromApiResponse(projectsPagedData);
    if (allList.length > 0 && pagedList.length > 0) {
      const byId = new Map();
      for (const p of allList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id) byId.set(id, p);
      }
      for (const p of pagedList) {
        const id = String(p?.id ?? p?.projectId ?? "");
        if (id && !byId.has(id)) byId.set(id, p);
      }
      return { list: Array.from(byId.values()) };
    }
    return projectsAllListData ?? projectsPagedData;
  }, [projectsAllListData, projectsPagedData]);
  const clientsData = useSelector(selectClientsData);
  const projectsLoading = useSelector(selectProjectsLoading);
  const usersData = useSelector(selectUsersData);
  const [workItemTypeList, setWorkItemTypeList] = useState([]);
  const [priorityList, setPriorityList] = useState([]);
  const [severityList, setSeverityList] = useState([]);
  const [environmentOptions, setEnvironmentOptions] = useState(ENVIRONMENT_FALLBACK_OPTIONS);
  const [, setSubmitting] = useState(false);
  const [parentWorkItemOptions, setParentWorkItemOptions] = useState([]);
  const [parentWorkItemsLoading, setParentWorkItemsLoading] = useState(false);
  const updateBaselineRef = useRef(null);
  const prevProjectIdForParentResetRef = useRef(null);

  const projectsList = preferListFromPropOrStore(projectsListProp, projectsFromStore);
  const clientsList = preferListFromPropOrStore(clientsListProp, clientsData);

  // Ensure project and client lists are loaded for Project (Parent) dropdown
  useEffect(() => {
    if (!projectsList?.length) dispatch(fetchProjectsData({ getAll: true }));
    if (!clientsList?.length) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, projectsList?.length, clientsList?.length]);

  // Always hit GET /users/all when the create form mounts so assignee options match latest users.
  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true, force: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("WORK_ITEM_TYPE"))
      .unwrap()
      .then((list) => setWorkItemTypeList(getListFromApiResponse(list)))
      .catch(() => setWorkItemTypeList([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("PRIORITY"))
      .unwrap()
      .then((list) => {
        const mapped = getListFromApiResponse(list);
        setPriorityList(mapped);
      })
      .catch(() => {
        setPriorityList([]);
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("SEVERITY"))
      .unwrap()
      .then((list) => setSeverityList(getListFromApiResponse(list)))
      .catch(() => setSeverityList([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("ENVIRONMENT"))
      .unwrap()
      .then((list) => {
        const mapped = getListFromApiResponse(list).map((item) =>
          mapSystemListItemToOption(item, {})
        );
        setEnvironmentOptions(mapped.length > 0 ? mapped : ENVIRONMENT_FALLBACK_OPTIONS);
      })
      .catch(() => {
        setEnvironmentOptions(ENVIRONMENT_FALLBACK_OPTIONS);
      });
  }, [dispatch]);

  const allowedProjects = getAllowedProjects(projectsList, clientsList, isClientOnHold);
  /** Creating from a project route/board — lock project to that context (global create passes no id). */
  const isProjectScoped = Boolean(currentProjectId && String(currentProjectId).trim());
  const isEditMode = Boolean(
    editContext?.workItemId != null && String(editContext.workItemId).trim() !== ""
  );
  const baseProjectOptions = allowedProjects
    .map((p) => mapProjectToParentOption(p))
    .filter((o) => o.value !== "" && o.value != null);
  // Ensure the currently-selected project (from URL) is present in options so the dropdown
  // can display the project name even if it was filtered out by allowedProjects.
  const currentProjectOption = currentProjectId
    ? projectsList
        .map((p) => mapProjectToParentOption(p))
        .find((o) => String(o.value) === String(currentProjectId))
    : null;
  const projectOptions =
    currentProjectOption &&
    !baseProjectOptions.some((o) => String(o.value) === String(currentProjectOption.value))
      ? [currentProjectOption, ...baseProjectOptions]
      : baseProjectOptions;
  const projectsLoadingOrEmpty = projectsLoading || !projectsList?.length;

  const typeOptions =
    workItemTypeList.length === 0
      ? TYPE_FALLBACK_OPTIONS
      : workItemTypeList.map((item) => mapSystemListItemToOption(item, TYPE_ICON_MAP));

  const priorityOptions =
    priorityList.length === 0
      ? PRIORITY_FALLBACK_OPTIONS
      : priorityList.map((item) => mapSystemListItemToOption(item, PRIORITY_ICON_MAP));

  const severityOptions = severityList.map((item) => mapSystemListItemToOption(item, {}));

  const [description, setDescription] = useState("");

  const typeIdsRequiringParentEpic = useMemo(() => [], []);

  const createTaskValidationSchema = useMemo(
    () => buildCreateTaskSchema({ typeIdsRequiringParent: typeIdsRequiringParentEpic }),
    [typeIdsRequiringParentEpic]
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    reset,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createTaskValidationSchema),
    shouldUseNativeValidation: false,
    defaultValues: buildBacklogFormEmptyValues(currentProjectId),
  });

  useEffect(() => {
    const pid = editContext?.projectId;
    const wid = editContext?.workItemId;
    if (pid == null || wid == null || String(pid).trim() === "" || String(wid).trim() === "") {
      updateBaselineRef.current = null;
      return undefined;
    }
    const cachedWi = editContext?.normalizedWorkItem;
    let cancelled = false;
    (async () => {
      try {
        let wi;
        if (cachedWi != null && typeof cachedWi === "object") {
          wi = cachedWi;
        } else {
          const raw = await dispatch(
            fetchWorkItemById({
              projectId: String(pid).trim(),
              workItemId: String(wid).trim(),
            })
          ).unwrap();
          if (cancelled) return;
          wi = mapWorkItemFromApiPayload(raw);
        }
        if (cancelled) return;
        updateBaselineRef.current = extractUpdateBaselineFromWorkItem(wi);
        const { formPartial, description: descBody } = mapWorkItemToCreateTaskDefaults(wi);
        let est = formPartial.originalEstimate ?? "";
        if (String(est).trim()) {
          const pr = parseWorkItemEstimateInputToMinutes(String(est).trim());
          if (!pr.ok) est = "";
        }
        const base = buildBacklogFormEmptyValues(currentProjectId);
        const proj =
          (formPartial.project && String(formPartial.project).trim()) ||
          (currentProjectId && String(currentProjectId).trim()) ||
          base.project;
        const summaryRaw = (formPartial.summary ?? "").trim();
        /** Backlog modal: checkbox defaults checked unless API explicitly says not backlog. */
        const backlogExplicitlyOff =
          wi.isBacklog === false ||
          wi.isBacklog === 0 ||
          String(wi.isBacklog).toLowerCase() === "false" ||
          wi.is_backlog === false ||
          wi.is_backlog === 0;
        const isBacklogResolved = backlogExplicitlyOff ? false : true;
        reset({
          ...base,
          ...formPartial,
          originalEstimate: est,
          project: proj,
          startDate:
            formPartial.startDate && String(formPartial.startDate).trim()
              ? String(formPartial.startDate).trim()
              : base.startDate,
          dueDate:
            formPartial.dueDate && String(formPartial.dueDate).trim()
              ? String(formPartial.dueDate).trim()
              : "",
          summary: summaryRaw,
          priority:
            formPartial.priority && String(formPartial.priority).trim()
              ? String(formPartial.priority).trim()
              : base.priority,
          type:
            formPartial.type && String(formPartial.type).trim()
              ? String(formPartial.type).trim()
              : base.type,
          isBacklog: isBacklogResolved,
        });
        prevProjectIdForParentResetRef.current =
          proj != null && String(proj).trim() !== "" ? String(proj).trim() : null;
        setDescription(descBody);
        await trigger();
      } catch (err) {
        if (!cancelled) {
          toast.error(getThunkRejectMessage(err, "Failed to load work item for editing."));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    editContext?.projectId,
    editContext?.workItemId,
    editContext?.normalizedWorkItem,
    reset,
    trigger,
    currentProjectId,
  ]);

  // Resolve project ID for work-item fetch (selected project or current project from URL)
  const selectedProjectId =
    watch("project")?.trim() || (currentProjectId && String(currentProjectId).trim()) || null;

  const statusesResponse = useSelector((state) =>
    selectedProjectId ? selectProjectStatusesResponse(state, selectedProjectId) : null
  );

  useEffect(() => {
    if (!selectedProjectId) return;
    dispatch(fetchProjectStatuses({ projectId: selectedProjectId, context: "backlog-edit" }));
  }, [dispatch, selectedProjectId]);

  useEffect(() => {
    const next = selectedProjectId?.trim() || null;
    const prev = prevProjectIdForParentResetRef.current;
    prevProjectIdForParentResetRef.current = next;
    if (prev != null && next !== prev) {
      setValue("parentId", "", { shouldValidate: false, shouldDirty: false });
      clearErrors("parentId");
    }
  }, [selectedProjectId, setValue, clearErrors]);

  // Parent (Epic) dropdown — GET /projects/:projectId/work-items/epics (Bearer on ApiBase)
  useEffect(() => {
    if (!selectedProjectId) {
      setParentWorkItemOptions([]);
      return;
    }
    setParentWorkItemsLoading(true);
    const endpoint = API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_EPICS(selectedProjectId);
    get(endpoint)
      .then((response) => {
        const raw = response?.data ?? response;
        const dataWrapper = raw?.data ?? raw;
        const rawList = Array.isArray(dataWrapper)
          ? dataWrapper
          : Array.isArray(dataWrapper?.data)
            ? dataWrapper.data
            : [];
        const list = rawList.map(normalizeWorkItemFromApi);
        const options = (list || []).map((item) => ({
          value: item.id,
          label: `${item.issueKey || item.itemKey || item.id} - ${item.title || "Untitled"}`,
        }));
        setParentWorkItemOptions(options);
      })
      .catch(() => setParentWorkItemOptions([]))
      .finally(() => setParentWorkItemsLoading(false));
  }, [selectedProjectId]);

  // When opened from a project's Work Items tab, pre-fill project from URL id
  useEffect(() => {
    if (currentProjectId && String(currentProjectId).trim()) {
      setValue("project", String(currentProjectId).trim(), { shouldValidate: false });
    }
  }, [currentProjectId, setValue]);

  const typeValue = watch("type");
  const selectedTypeLabel = typeOptions.find((o) => o.value === typeValue)?.label ?? "";

  const assigneeOptionsWithAvatar = useMemo(() => {
    const users = getListFromApiResponse(usersData);
    const list = filterAssigneesForWorkItemType(users, selectedTypeLabel);
    return list.map(mapUserToAssigneeOptionForDropdown).filter((o) => o.value);
  }, [usersData, selectedTypeLabel]);

  useEffect(() => {
    if (!isEpicWorkItemTypeLabel(selectedTypeLabel)) return;
    const current = getValues("assignee");
    if (!current) return;
    const allowed = assigneeOptionsWithAvatar.some((o) => o.value === String(current));
    if (!allowed) {
      setValue("assignee", "", { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedTypeLabel, assigneeOptionsWithAvatar, getValues, setValue]);

  useEffect(() => {
    const mediumId = resolveDefaultMediumPriorityId(priorityList);
    if (!mediumId) return;
    const current = getValues("priority");
    if (current === undefined || current === null || String(current).trim() === "") {
      setValue("priority", mediumId, { shouldValidate: true, shouldDirty: false });
    }
  }, [priorityList, getValues, setValue]);

  useEffect(() => {
    if (selectedTypeLabel !== "Task" && selectedTypeLabel !== "Bug") {
      setValue("parentId", "", { shouldValidate: true });
    }
  }, [selectedTypeLabel, setValue]);

  const todayDate = getTodayLocalDate();
  const startDateValue = watch("startDate");
  const dueDateMin = startDateValue && startDateValue >= todayDate ? startDateValue : todayDate;

  const originalEstimateRegister = register("originalEstimate");

  const onSubmit = async (data) => {
    const workItemId = editContext?.workItemId != null ? String(editContext.workItemId).trim() : "";
    const sourceProjectId = String(
      editContext?.projectId ?? currentProjectId ?? data.project ?? ""
    ).trim();
    const targetProjectId = data.project?.trim() || sourceProjectId;
    if (!targetProjectId) {
      toast.error("Please select a project.");
      return;
    }
    if (!sourceProjectId) {
      toast.error("Missing source project.");
      return;
    }
    if (!workItemId) {
      toast.error("Missing work item id.");
      return;
    }
    const baseline = updateBaselineRef.current;
    if (!baseline) {
      toast.error("Work item is still loading. Wait a moment and try again.");
      return;
    }
    if (projectsList.length > 0 && clientsList.length > 0) {
      const project = projectsList.find(
        (p) =>
          String(p.id ?? p.projectId) === String(targetProjectId) ||
          (p.id ?? p.projectId) === targetProjectId
      );
      if (project) {
        const client = clientsList.find((c) => c.clientName === project.clientName);
        if (client && isClientOnHold(client)) {
          toast.error("Cannot save. This project's client is On Hold.");
          return;
        }
      }
    }

    const estimateTrimmed = String(data.originalEstimate ?? "").trim();
    if (estimateTrimmed) {
      const parsed = parseWorkItemEstimateInputToMinutes(estimateTrimmed);
      if (!parsed.ok) {
        toast.error(parsed.error || "Invalid original estimate.");
        return;
      }
    }

    let targetStatusesPayload = null;
    if (targetProjectId !== sourceProjectId) {
      targetStatusesPayload = statusesResponse;
      if (!getProjectWorkflowStatusesList(targetStatusesPayload).length) {
        try {
          targetStatusesPayload = await dispatch(
            fetchProjectStatuses({
              projectId: targetProjectId,
              context: "backlog-edit-save",
            })
          ).unwrap();
        } catch {
          toast.error("Could not load statuses for the selected project.");
          return;
        }
      }
    }

    const built = buildUpdateWorkItemBodyFromBacklogForm({
      data,
      description,
      baseline,
      typeOptions,
      priorityList,
      priorityOptions,
      targetProjectId,
      sourceProjectId,
      targetStatusesPayload,
    });
    if (!built.ok) {
      toast.error(built.error || "Invalid form data.");
      return;
    }

    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const attachmentFiles = (Array.isArray(data.attachments) ? data.attachments : [])
        .map((item) => item?.file ?? item)
        .filter(Boolean);

      await dispatch(
        updateWorkItem({
          projectId: String(sourceProjectId),
          workItemId,
          body: built.body,
          ...(attachmentFiles.length > 0 ? { files: attachmentFiles } : {}),
        })
      ).unwrap();

      toast.success("Work item updated successfully.");
      onSuccess?.();
    } catch (err) {
      if (isWorkItemVersionConflictError(err)) {
        toast.error("This work item was updated elsewhere. Reload the form and try again.");
        return;
      }
      toast.error(getThunkRejectMessage(err, "Failed to update work item."));
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  return (
    <div className="space-y-5">
      <form
        id="backlog-edit-task-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
      >
        <div className="w-2/4">
          <Controller
            name="project"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={isProjectScoped && !isEditMode ? "Project (current)" : "Project"}
                required
                disabled={isProjectScoped && !isEditMode}
                clearable={!isProjectScoped || isEditMode}
                options={projectOptions}
                searchable={true}
                placeholder={
                  projectsLoadingOrEmpty && projectOptions.length === 0
                    ? "Loading projects..."
                    : "Select project"
                }
              />
            )}
          />
          <ErrorMsg message={errors.project?.message} />
        </div>

        <div className="w-2/4">
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={
                  <>
                    Work type
                    <Typography variant="span1" className="text-red-600 pl-1" component="span">
                      *
                    </Typography>
                  </>
                }
                options={typeOptions}
              />
            )}
          />
          <ErrorMsg message={errors.type?.message} />
        </div>

        {(selectedTypeLabel === "Task" || selectedTypeLabel === "Bug") && (
          <div className="w-2/4">
            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  label={
                    <>
                      Parent (Epic)
                      <Typography variant="span1" className="text-red-600 pl-1" component="span">
                        *
                      </Typography>
                    </>
                  }
                  required
                  options={parentWorkItemOptions}
                  placeholder={
                    parentWorkItemsLoading ? "Loading..." : "Select epic (required for Task/Bug)"
                  }
                  disabled={parentWorkItemsLoading || !selectedProjectId}
                />
              )}
            />
            <ErrorMsg message={errors.parentId?.message} />
          </div>
        )}

        <div className="w-full">
          <Typography className="font-medium mb-1">
            Summary
            <Typography variant="span1" className="text-red-600 pl-1" component="span">
              *
            </Typography>
          </Typography>

          <Input
            {...register("summary")}
            className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
              errors.summary ? "border-badge-danger" : ""
            }`}
            placeholder="Enter task summary"
            noWrapper={true}
          />

          <ErrorMsg message={errors.summary?.message} />
        </div>

        {/* Bug-only fields: Reason of Bug, Severity, Environment, Reproduction Steps */}
        {selectedTypeLabel === "Bug" && (
          <>
            <div className="w-2/4">
              <Controller
                name="reasonOfBug"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Reason of Bug"
                    options={REASON_OF_BUG_OPTIONS}
                    placeholder="Select..."
                  />
                )}
              />
            </div>
            <div className="w-2/4">
              <Controller
                name="severityId"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Severity"
                    options={severityOptions}
                    placeholder="Select severity"
                  />
                )}
              />
            </div>
            <div className="w-2/4">
              <Controller
                name="environment"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    label="Environment"
                    options={environmentOptions}
                    placeholder="Select environment(s)"
                    multiselect
                    allowAddOption
                    onAddOption={(newOption) => {
                      setEnvironmentOptions((prev) => [...prev, newOption]);
                    }}
                  />
                )}
              />
            </div>
            <div className="w-full">
              <Input
                label="Reproduction Steps"
                type="textarea"
                rows={5}
                {...register("reproductionSteps")}
                className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary min-h-[100px] resize-y"
                placeholder="Describe steps to reproduce (multiple lines)"
              />
            </div>
          </>
        )}

        <div className="w-2/4">
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label={
                  <>
                    Priority
                    <Typography variant="span1" className="text-red-600 pl-1" component="span">
                      *
                    </Typography>
                  </>
                }
                options={priorityOptions}
                placeholder="Select priority"
              />
            )}
          />
          <ErrorMsg message={errors.priority?.message} />
        </div>
        <Controller
          name="assignee"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              label="Assignee"
              placeholder="Assign user..."
              options={assigneeOptionsWithAvatar}
              searchable={true}
            />
          )}
        />

        {/* label for epic and task */}
        {selectedTypeLabel !== "Bug" && (
          <div className="w-2/4">
            <Controller
              name="label"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  label="Labels"
                  options={LABEL_FALLBACK_OPTIONS}
                  placeholder="Select label"
                  // searchable
                  multiselect
                />
              )}
            />
          </div>
        )}
        {/* end label for epic and task */}

        <DescriptionEditor value={description} onChange={setDescription} />

        <FileDropzone
          label="Attachments"
          onFilesChange={(files) => setValue("attachments", files)}
          maxFiles={10}
          maxSizeMB={10}
          inspectContent={false}
        />

        <div className="w-2/4">
          <Input
            label="Original estimate"
            {...originalEstimateRegister}
            type="text"
            autoComplete="off"
            className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
            placeholder="e.g. 2w, 5h, 1d 2h, 120m"
            error={errors.originalEstimate?.message}
            onChange={(e) => {
              const next = sanitizeWorkItemEstimateInput(e.target.value);
              // Uncontrolled register() — browser keeps typed chars unless we sync the DOM.
              e.target.value = next;
              originalEstimateRegister.onChange({
                ...e,
                target: { ...e.target, value: next, name: e.target.name },
              });
            }}
          />
        </div>
        {/* <div className="w-2/4">
                    <Input
                        label="Story Points"
                        {...register("storyPoints")}
                        type="number"
                        min={0}
                        className="w-2/4 border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                        placeholder="e.g. 5"
                        noWrapper={true}
                    />
                </div> */}
        <div className="w-2/4">
          <Typography variant="label" className="block mb-1 text-sm font-medium text-text-primary">
            Start Date
            <Typography variant="span1" className="text-red-600 pl-1" component="span">
              *
            </Typography>
          </Typography>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <SingleDatePicker
                value={field.value ? { date: field.value } : null}
                onChange={(val) => field.onChange(val?.date ?? "")}
                minDate={todayDate}
                placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                className={errors.startDate ? "!border-red-500" : ""}
              />
            )}
          />
          <ErrorMsg message={errors.startDate?.message} />
        </div>
        <div className="w-2/4">
          <Typography variant="label" className="block mb-1 text-sm font-medium text-text-primary">
            Due Date
            <Typography
              variant="span1"
              className="text-text-secondary pl-1 font-normal"
              component="span"
            >
              (optional)
            </Typography>
          </Typography>
          <Controller
            name="dueDate"
            control={control}
            render={({ field }) => (
              <SingleDatePicker
                value={field.value ? { date: field.value } : null}
                onChange={(val) => field.onChange(val?.date ?? "")}
                minDate={dueDateMin}
                placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                className={errors.dueDate ? "!border-red-500" : ""}
              />
            )}
          />
          <ErrorMsg message={errors.dueDate?.message} />
        </div>

        <Controller
          name="isBacklog"
          control={control}
          render={({ field }) => (
            <Checkbox
              name={field.name}
              id="backlog-edit-is-backlog"
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              label="Work item is in backlog"
            />
          )}
        />
      </form>
    </div>
  );
};

export default BacklogTaskForm;
